import type { V10ControlRoomSection, V10ProjectMode } from "../adapters/v10-phase-model.js";
import {
  isV10ProjectMode,
  normalizeV10ControlRoomSection,
} from "../adapters/v10-phase-model.js";
import type { V10View } from "./v10-views.js";
import { isV10View, isV10ViewExposed } from "./v10-views.js";

export type V10SheetId = "new-work";

export type V10IntakeFocus = "relaunch" | "external-proof";

export interface V10RouteState {
  readonly view: V10View;
  readonly projectId: string | null;
  readonly section: V10ControlRoomSection | null;
  readonly sheet: V10SheetId | null;
  readonly mode: V10ProjectMode | null;
  readonly intake: V10IntakeFocus | null;
}

export type V10RoutePatch = Partial<V10RouteState>;

export function parseV10Route(search: string): V10RouteState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const viewRaw = params.get("view") ?? "today";
  const parsedView = isV10View(viewRaw) ? viewRaw : "today";
  const view = isV10ViewExposed(parsedView) ? parsedView : "today";
  const sectionRaw = params.get("section");
  const modeRaw = params.get("mode");
  const sheetRaw = params.get("sheet");
  const intakeRaw = params.get("intake");

  return {
    view,
    projectId: params.get("project")?.trim() || null,
    section: sectionRaw ? normalizeV10ControlRoomSection(sectionRaw) : null,
    sheet: sheetRaw === "new-work" ? "new-work" : null,
    mode: modeRaw && isV10ProjectMode(modeRaw) ? modeRaw : null,
    intake:
      intakeRaw === "relaunch" || intakeRaw === "external-proof" ? intakeRaw : null,
  };
}

export function buildV10Search(state: V10RouteState): string {
  const params = new URLSearchParams();
  if (state.view !== "today") {
    params.set("view", state.view);
  }
  if (state.projectId) {
    params.set("project", state.projectId);
  }
  if (state.section) {
    params.set("section", state.section);
  }
  if (state.sheet) {
    params.set("sheet", state.sheet);
  }
  if (state.mode) {
    params.set("mode", state.mode);
  }
  if (state.intake) {
    params.set("intake", state.intake);
  }
  const q = params.toString();
  return q.length > 0 ? `?${q}` : "";
}

export function mergeV10Route(current: V10RouteState, patch: V10RoutePatch): V10RouteState {
  const next: V10RouteState = {
    view: patch.view ?? current.view,
    projectId: patch.projectId !== undefined ? patch.projectId : current.projectId,
    section: patch.section !== undefined ? patch.section : current.section,
    sheet: patch.sheet !== undefined ? patch.sheet : current.sheet,
    mode: patch.mode !== undefined ? patch.mode : current.mode,
    intake: patch.intake !== undefined ? patch.intake : current.intake,
  };

  if (next.view === "project" && next.section === undefined && patch.section === undefined) {
    return { ...next, section: null };
  }
  if (next.view !== "project") {
    return { ...next, section: null };
  }
  return next;
}

export function pushV10Route(current: V10RouteState, patch: V10RoutePatch): V10RouteState {
  const next = mergeV10Route(current, patch);
  const search = buildV10Search(next);
  const url = `${window.location.pathname}${search}${window.location.hash}`;
  window.history.pushState(null, "", url);
  return next;
}

export type V10Navigate = (patch: V10RoutePatch) => void;
